import React, { useState, useEffect } from 'react';
import { auditsAPI, controlsAPI, departmentsAPI, observationsAPI, findingsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  HelpCircle,
  Minus,
  Save,
  FileText,
  Shield,
  Loader2,
} from 'lucide-react';

const AuditExecutionPage = () => {
  const [audits, setAudits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [controls, setControls] = useState([]);
  const [observations, setObservations] = useState([]);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedControl, setSelectedControl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [findingDialogOpen, setFindingDialogOpen] = useState(false);

  const [currentObservation, setCurrentObservation] = useState({
    audit_remark: '',
    compliance_status: 'Pending',
    auditor_comment: '',
  });

  const [findingData, setFindingData] = useState({
    severity: 'Medium',
    observation: '',
    recommendation: '',
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedAudit && selectedDepartment) {
      fetchObservations();
    }
  }, [selectedAudit, selectedDepartment]);

  const fetchInitialData = async () => {
    try {
      const [auditsRes, deptsRes, controlsRes] = await Promise.all([
        auditsAPI.getAll(),
        departmentsAPI.getAll(),
        controlsAPI.getAll(),
      ]);
      setAudits(auditsRes.data);
      setDepartments(deptsRes.data);
      setControls(controlsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchObservations = async () => {
    try {
      const res = await observationsAPI.getAll({
        audit_id: selectedAudit.id,
        department_id: selectedDepartment.id,
      });
      setObservations(res.data);
    } catch (error) {
      console.error('Error fetching observations:', error);
    }
  };

  const handleControlSelect = (control) => {
    setSelectedControl(control);
    const existingObs = observations.find(
      (o) => o.control_id === control.id
    );
    if (existingObs) {
      setCurrentObservation({
        audit_remark: existingObs.audit_remark || '',
        compliance_status: existingObs.compliance_status || 'Pending',
        auditor_comment: existingObs.auditor_comment || '',
        id: existingObs.id,
      });
    } else {
      setCurrentObservation({
        audit_remark: '',
        compliance_status: 'Pending',
        auditor_comment: '',
      });
    }
  };

  const handleSaveObservation = async () => {
    if (!selectedAudit || !selectedDepartment || !selectedControl) return;

    setSaving(true);
    try {
      const payload = {
        audit_id: selectedAudit.id,
        department_id: selectedDepartment.id,
        control_id: selectedControl.id,
        audit_remark: currentObservation.audit_remark,
        compliance_status: currentObservation.compliance_status,
        auditor_comment: currentObservation.auditor_comment,
      };

      if (currentObservation.id) {
        await observationsAPI.update(currentObservation.id, payload);
        toast.success('Observation updated');
      } else {
        const res = await observationsAPI.create(payload);
        setCurrentObservation((prev) => ({ ...prev, id: res.data.id }));
        toast.success('Observation saved');
      }
      fetchObservations();
    } catch (error) {
      toast.error('Failed to save observation');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateFinding = async () => {
    if (!selectedControl || !selectedDepartment) return;

    try {
      await findingsAPI.create({
        observation_id: currentObservation.id,
        control_id: selectedControl.id,
        department_id: selectedDepartment.id,
        severity: findingData.severity,
        observation: findingData.observation,
        recommendation: findingData.recommendation,
        status: 'Open',
      });
      toast.success('Finding created');
      setFindingDialogOpen(false);
      setFindingData({ severity: 'Medium', observation: '', recommendation: '' });
    } catch (error) {
      toast.error('Failed to create finding');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Compliant':
        return <CheckCircle2 size={16} className="text-emerald-500" />;
      case 'Non-Compliant':
        return <AlertTriangle size={16} className="text-red-500" />;
      case 'Partially Compliant':
        return <HelpCircle size={16} className="text-amber-500" />;
      case 'Not Applicable':
        return <Minus size={16} className="text-slate-400" />;
      default:
        return <div className="w-4 h-4 rounded-full border-2 border-slate-300" />;
    }
  };

  const getControlStatus = (controlId) => {
    const obs = observations.find((o) => o.control_id === controlId);
    return obs?.compliance_status || 'Pending';
  };

  const groupedControls = controls.reduce((acc, control) => {
    if (!acc[control.category]) {
      acc[control.category] = [];
    }
    acc[control.category].push(control);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-[#4A5690]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-execution-page">
      {/* Selection Row */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-slate-500 mb-1 block">Select Audit</Label>
              <Select
                value={selectedAudit?.id || ''}
                onValueChange={(value) => {
                  setSelectedAudit(audits.find((a) => a.id === value));
                  setSelectedDepartment(null);
                  setSelectedControl(null);
                }}
              >
                <SelectTrigger data-testid="select-audit">
                  <SelectValue placeholder="Choose an audit" />
                </SelectTrigger>
                <SelectContent>
                  {audits.map((audit) => (
                    <SelectItem key={audit.id} value={audit.id}>
                      {audit.audit_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-slate-500 mb-1 block">Select Department</Label>
              <Select
                value={selectedDepartment?.id || ''}
                onValueChange={(value) => {
                  setSelectedDepartment(departments.find((d) => d.id === value));
                  setSelectedControl(null);
                }}
                disabled={!selectedAudit}
              >
                <SelectTrigger data-testid="select-department">
                  <SelectValue placeholder="Choose a department" />
                </SelectTrigger>
                <SelectContent>
                  {departments
                    .filter((d) => selectedAudit?.departments?.includes(d.id))
                    .map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      {selectedAudit && selectedDepartment ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Controls List */}
          <Card className="lg:col-span-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Shield size={18} className="text-[#4A5690]" />
                ISO Controls
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[600px]">
                {Object.entries(groupedControls).map(([category, categoryControls]) => (
                  <div key={category} className="border-b last:border-b-0">
                    <div className="px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                      {category}
                    </div>
                    {categoryControls.map((control) => {
                      const status = getControlStatus(control.id);
                      const isSelected = selectedControl?.id === control.id;
                      return (
                        <button
                          key={control.id}
                          onClick={() => handleControlSelect(control)}
                          className={`w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-slate-50 transition-colors ${
                            isSelected ? 'bg-[#4A5690]/5 border-l-4 border-[#4A5690]' : ''
                          }`}
                          data-testid={`control-${control.control_id}`}
                        >
                          {getStatusIcon(status)}
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-[#4A5690]">
                              {control.control_id}
                            </div>
                            <div className="text-sm text-slate-700 truncate">
                              {control.control_title}
                            </div>
                          </div>
                          <ChevronRight size={16} className="text-slate-400" />
                        </button>
                      );
                    })}
                  </div>
                ))}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Control Details */}
          <Card className="lg:col-span-8">
            {selectedControl ? (
              <>
                <CardHeader className="border-b">
                  <div className="flex items-start justify-between">
                    <div>
                      <Badge variant="outline" className="mb-2">
                        {selectedControl.control_id}
                      </Badge>
                      <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {selectedControl.control_title}
                      </CardTitle>
                      <p className="text-sm text-slate-500 mt-1">
                        Category: {selectedControl.category}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6 pt-6">
                  {/* Audit Question */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <Label className="text-xs text-slate-500 uppercase tracking-wide">
                      Audit Question
                    </Label>
                    <p className="mt-1 text-slate-800">{selectedControl.audit_question}</p>
                  </div>

                  {/* Evidence Required */}
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                    <Label className="text-xs text-amber-700 uppercase tracking-wide">
                      Evidence Required
                    </Label>
                    <p className="mt-1 text-amber-900">{selectedControl.evidence_required}</p>
                  </div>

                  {/* Compliance Status */}
                  <div className="space-y-2">
                    <Label>Compliance Status</Label>
                    <Select
                      value={currentObservation.compliance_status}
                      onValueChange={(value) =>
                        setCurrentObservation({ ...currentObservation, compliance_status: value })
                      }
                    >
                      <SelectTrigger data-testid="compliance-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="Compliant">Compliant</SelectItem>
                        <SelectItem value="Partially Compliant">Partially Compliant</SelectItem>
                        <SelectItem value="Non-Compliant">Non-Compliant</SelectItem>
                        <SelectItem value="Not Applicable">Not Applicable</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Audit Remark */}
                  <div className="space-y-2">
                    <Label htmlFor="audit_remark">Audit Remark</Label>
                    <Textarea
                      id="audit_remark"
                      value={currentObservation.audit_remark}
                      onChange={(e) =>
                        setCurrentObservation({ ...currentObservation, audit_remark: e.target.value })
                      }
                      placeholder="Enter your audit observations..."
                      rows={3}
                      data-testid="audit-remark-input"
                    />
                  </div>

                  {/* Auditor Comment */}
                  <div className="space-y-2">
                    <Label htmlFor="auditor_comment">Auditor Comment</Label>
                    <Textarea
                      id="auditor_comment"
                      value={currentObservation.auditor_comment}
                      onChange={(e) =>
                        setCurrentObservation({ ...currentObservation, auditor_comment: e.target.value })
                      }
                      placeholder="Additional comments..."
                      rows={2}
                      data-testid="auditor-comment-input"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t">
                    <Button
                      onClick={handleSaveObservation}
                      className="bg-[#4A5690] hover:bg-[#3D477A] btn-scale"
                      disabled={saving}
                      data-testid="save-observation-btn"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                      Save Observation
                    </Button>

                    {currentObservation.compliance_status === 'Non-Compliant' && (
                      <Button
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        onClick={() => setFindingDialogOpen(true)}
                        data-testid="create-finding-btn"
                      >
                        <AlertTriangle size={16} className="mr-2" />
                        Create Finding
                      </Button>
                    )}
                  </div>
                </CardContent>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-[600px] text-slate-500">
                <FileText size={48} className="text-slate-300 mb-4" />
                <p>Select a control to start auditing</p>
              </div>
            )}
          </Card>
        </div>
      ) : (
        <Card>
          <CardContent className="py-16 text-center text-slate-500">
            <Shield size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium">Select an audit and department to begin</p>
            <p className="text-sm mt-1">Choose from the dropdowns above to start the audit process</p>
          </CardContent>
        </Card>
      )}

      {/* Finding Dialog */}
      <Dialog open={findingDialogOpen} onOpenChange={setFindingDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Create Finding
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Severity</Label>
              <Select
                value={findingData.severity}
                onValueChange={(value) => setFindingData({ ...findingData, severity: value })}
              >
                <SelectTrigger data-testid="finding-severity-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="finding_observation">Observation</Label>
              <Textarea
                id="finding_observation"
                value={findingData.observation}
                onChange={(e) => setFindingData({ ...findingData, observation: e.target.value })}
                placeholder="Describe the finding..."
                rows={3}
                data-testid="finding-observation-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="finding_recommendation">Recommendation</Label>
              <Textarea
                id="finding_recommendation"
                value={findingData.recommendation}
                onChange={(e) => setFindingData({ ...findingData, recommendation: e.target.value })}
                placeholder="Suggested corrective action..."
                rows={3}
                data-testid="finding-recommendation-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setFindingDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleCreateFinding}
                className="flex-1 bg-[#4A5690] hover:bg-[#3D477A]"
                data-testid="submit-finding-btn"
              >
                Create Finding
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditExecutionPage;
